import {makeObservable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {Nullable} from '../../../utils/utility-types';
import type {DateTimeModel} from '../Date/DateTime.model';
import type {FileModel} from '../File/File.model';

import {BaseModel} from '../../../core/domain/Base/Base.model';

const ModelName = 'FoodDiaryModel';

export class FoodDiaryModel extends BaseModel {
  static ORDER_BY_MEAL_TYPE: Record<FoodDiaryModel.MealType, number> = {
    breakfast: 0,
    dinner: 2,
    lunch: 1,
    other: 4,
    snacks: 3,
  };
  avatar: Nullable<FileModel>;
  comment: string;
  /**
   * Datetime of meal if the user tracked it
   */
  consumed: Nullable<DateTimeModel>;
  /**
   * Date of meal (without time)
   */
  consumedDate: DateTimeModel;
  foods: FoodDiaryModel.Food[];
  nutrients: FoodDiaryModel.Nutrients;
  title: string;
  type: FoodDiaryModel.MealType;
  userId: string;

  constructor(props: FoodDiaryModel.Props) {
    super(props);
    Object.assign(this, props);

    makeObservable(
      this,
      BaseModel.mobxAnnotationFactory<FoodDiaryModel, FoodDiaryModel.Props>(
        props,
      ),
    );
  }

  get isCustomMeal() {
    return this.type === 'other';
  }

  get mealTypeTitle() {
    return FoodDiaryModel.getTitlesByMealType()[this.type];
  }

  static override get modelName(): typeof ModelName {
    return ModelName;
  }

  get modelName(): typeof ModelName {
    return ModelName;
  }

  get order() {
    return FoodDiaryModel.ORDER_BY_MEAL_TYPE[this.type];
  }

  static getTitlesByMealType = (): Record<FoodDiaryModel.MealType, string> => ({
    breakfast: i18n.t('shared:breakfast'),
    dinner: i18n.t('shared:dinner'),
    lunch: i18n.t('shared:lunch'),
    other: i18n.t('shared:other'),
    snacks: i18n.t('shared:snacks'),
  });
}

export namespace FoodDiaryModel {
  export type Food = {
    amount: number;
    foodId: string;
    servingUnit?: string;
    title: string;
  };

  export type MealType = 'breakfast' | 'dinner' | 'lunch' | 'other' | 'snacks';

  export type Nutrients = Partial<Record<NutrientSlug, number>>;

  export type NutrientSlug =
    | 'added_sugars'
    | 'calcium'
    | 'calories'
    | 'carbohydrate'
    | 'cholesterol'
    | 'fat'
    | 'fiber'
    | 'iron'
    | 'monounsaturated_fat'
    | 'polyunsaturated_fat'
    | 'potassium'
    | 'protein'
    | 'saturated_fat'
    | 'sodium'
    | 'sugar'
    | 'trans_fat'
    | 'vitamin_a'
    | 'vitamin_c'
    | 'vitamin_d';

  export type Props = BaseModel.ModelToProps<
    FoodDiaryModel,
    'isCustomMeal' | 'mealTypeTitle' | 'order'
  >;
}
